/**
 * AutomationContextoBadge — badge visual contextual (Sprint 3 / BUG-V2-058).
 *
 * Renderiza um chip pequeno identificando o contexto da automação.
 * Útil em:
 *   - Header de dialog (`AutomationDialogHeaderByContext`)
 *   - Coluna "Contexto" em `/admin/automacoes` (lista global)
 *   - Listas de automações dentro de cada contexto (informativo, redundante)
 *
 * Por kind:
 *   - `workflow`     → 🔄 Comercial / Aprovação (ou só workflow se sem coluna)
 *   - `task_type`    → 📋 Tipo Ligação (lookup activity_types)
 *   - `questionario` → ❓ Inspeção Anual / pergunta P1 (lookup template)
 *
 * Fallback gracioso: se lookup ainda carregando, mostra slug/id raw + label "Carregando".
 *
 * Visual distinguishable do badge "PROFILE" (Sprint 2) — usa variantes neutras +
 * ícone na esquerda + cor sutil. Não compete com badges de status.
 */

import { useMemo } from 'react';

import { Badge } from '@/components/ui';
import { useActivityType } from '@/lib/activity-types-store';
import { useQuestionarioTemplate } from '@/lib/questionarios-store';
import { useWorkflows } from '@/lib/workflows-store';
import { useColumnLabels } from '@/lib/use-column-labels';

import type { AutomationContexto } from './types';

interface AutomationContextoBadgeProps {
  contexto: AutomationContexto;
  /** Tamanho do badge. Default 'sm'. */
  size?: 'sm' | 'md';
  /** Mostrar texto resumido sem o "/" separador. Útil em espaços apertados. */
  compact?: boolean;
}

const KIND_ICON: Record<AutomationContexto['kind'], string> = {
  workflow: '🔄',
  task_type: '📋',
  questionario: '❓',
};

const KIND_LABEL: Record<AutomationContexto['kind'], string> = {
  workflow: 'Workflow',
  task_type: 'Tipo de Atividade',
  questionario: 'Questionário',
};

export function AutomationContextoBadge({
  contexto,
  size = 'sm',
  compact = false,
}: AutomationContextoBadgeProps) {
  // Resolve labels conforme kind. Cada hook só dispara quando relevante.
  const workflows = useWorkflows({ ativo: true });
  const workflowMatch = useMemo(
    () =>
      contexto.kind === 'workflow' || contexto.kind === 'questionario'
        ? workflows.data?.find((w) => w.slug === contexto.workflowSlug)
        : undefined,
    [workflows.data, contexto],
  );

  // Defaults vazios — getLabel cai no slug raw se não tiver override. Suficiente
  // pra UI de badge (qualquer label custom do admin sobrescreve o slug aqui).
  const columnLabel = useColumnLabels(
    contexto.kind === 'workflow' && contexto.columnSlug ? contexto.workflowSlug : 'unused',
    {},
  );

  const activityTypeQuery = useActivityType(
    contexto.kind === 'task_type' ? contexto.activityTypeId : null,
  );

  const questionarioQuery = useQuestionarioTemplate(
    contexto.kind === 'questionario' ? contexto.templateId : null,
  );

  const label = useMemo(() => {
    const icon = KIND_ICON[contexto.kind];

    if (contexto.kind === 'workflow') {
      const wfName = workflowMatch?.nome ?? contexto.workflowSlug;
      if (contexto.columnSlug) {
        const colName = columnLabel.getLabel(contexto.columnSlug);
        return `${icon} ${wfName}${compact ? ' · ' : ' / '}${colName}`;
      }
      return `${icon} ${wfName}`;
    }

    if (contexto.kind === 'task_type') {
      const name =
        activityTypeQuery.data?.label ??
        activityTypeQuery.data?.slug ??
        contexto.activityTypeId;
      return `${icon} ${name}`;
    }

    // questionario
    const tplName = questionarioQuery.data?.nome ?? contexto.templateId;
    if (contexto.perguntaCodigo) {
      return `${icon} ${tplName}${compact ? ' · ' : ' / '}pergunta ${contexto.perguntaCodigo}`;
    }
    return `${icon} ${tplName}`;
  }, [
    contexto,
    workflowMatch?.nome,
    columnLabel,
    activityTypeQuery.data,
    questionarioQuery.data,
    compact,
  ]);

  return (
    <Badge
      variant="neutral"
      size={size}
      data-testid={`automation-contexto-badge-${contexto.kind}`}
      title={`${KIND_LABEL[contexto.kind]}: ${label}`}
    >
      {label}
    </Badge>
  );
}
